import { useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { fitMercator, outlinePath, siteHighlighted } from './board-display';
import { BoardHeader } from './BoardHeader';
import {
  AGE_COLUMNS,
  AGE_FILTERS,
  AGE_LEGEND,
  AGE_VIEW,
  ALLOCATION,
  ALLOCATION_TITLE,
  ALLOCATION_TOTALS,
  JORDAN_OUTLINE,
  NEIGHBOURS,
  PRESENCE_INTRO,
  PRESENCE_NOTE,
  type PresenceAge,
  type PresenceFilter,
  SITES,
} from './content/presence';

const VIEWPORT = { width: 640, height: 600, pad: 48 };

/** Past this offset a label gets a leader line back to its point. */
const LEADER_FROM = 24;

/** Age markers: one series slot each, plus a mark shape so the age never reads by colour alone. */
/** The board's own age colours (see `AGE_COLOR` in content/ages.ts). */
const AGE_TOKEN: Readonly<Record<PresenceAge, string>> = { BRONZE: 'var(--age-bronze)', SILVER: 'var(--age-silver)', GOLDEN: 'var(--age-golden)' };

/**
 * Page 04 — our presence (plan §3.3): a bundled SVG of Jordan (no runtime fetch, no map library), the six sites by
 * Age with an age filter that dims the others, the cumulative location allocation and the per-age read-out.
 */
export const PresencePage = () => {
  const { t } = useI18nTranslation();
  const [filter, setFilter] = useState<PresenceFilter>('ALL');
  const project = useMemo(() => fitMercator(JORDAN_OUTLINE, VIEWPORT), []);
  const view = AGE_VIEW[filter];

  return (
    <div
      className="space-y-6"
      data-board-page="presence"
    >
      <BoardHeader
        eyebrow={PRESENCE_INTRO.eyebrow}
        title={PRESENCE_INTRO.title}
        body={PRESENCE_INTRO.body}
      />

      <div
        className="inline-flex overflow-hidden rounded-md border border-border"
        role="tablist"
        aria-label={t('board.presence.filter')}
      >
        {AGE_FILTERS.map((each) => (
          <button
            key={each.key}
            type="button"
            role="tab"
            aria-selected={filter === each.key}
            data-age-filter={each.key}
            onClick={() => setFilter(each.key)}
            className={cn(
              'border-s border-border px-3 py-1.5 text-sm first:border-s-0',
              filter === each.key ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-muted',
            )}
          >
            {each.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <figure className="rounded-lg border border-border bg-card p-3">
          <figcaption className="px-2 text-xs uppercase tracking-[0.14em] text-muted-foreground">{PRESENCE_INTRO.figure}</figcaption>
          <svg
            viewBox={`0 0 ${VIEWPORT.width} ${VIEWPORT.height}`}
            className="block h-auto w-full"
            role="img"
            aria-label={t('board.presence.map')}
            data-presence-map
          >
            <path
              d={outlinePath(JORDAN_OUTLINE, project)}
              fill="hsl(var(--muted))"
              stroke="hsl(var(--foreground) / 0.6)"
              strokeWidth={1.5}
              strokeLinejoin="round"
              data-jordan-outline
            />
            {NEIGHBOURS.map((neighbour) => {
              const [x, y] = project(neighbour.lon, neighbour.lat);

              return x > 0 && x < VIEWPORT.width && y > 0 && y < VIEWPORT.height ? (
                <text
                  key={neighbour.name}
                  x={x}
                  y={y}
                  fontSize={10}
                  letterSpacing={2}
                  fill="hsl(var(--muted-foreground) / 0.6)"
                >
                  {neighbour.name}
                </text>
              ) : null;
            })}
            {SITES.map((site) => {
              const [x, y] = project(site.lon, site.lat);
              const color = AGE_TOKEN[site.age];
              const anchor = site.labelDx < 0 ? 'end' : 'start';
              const dx = site.labelDx;
              const leader = Math.hypot(site.labelDx, site.labelDy) > LEADER_FROM;

              return (
                <g
                  key={site.name}
                  opacity={siteHighlighted(site.age, filter) ? 1 : 0.15}
                  data-site={site.name}
                  data-site-age={site.age}
                >
                  {leader ? (
                    <line
                      x1={x}
                      y1={y}
                      x2={x + dx - (dx < 0 ? -4 : 4)}
                      y2={y + site.labelDy - 4}
                      stroke="hsl(var(--muted-foreground))"
                      strokeWidth={0.75}
                    />
                  ) : null}
                  {site.current ? (
                    <circle
                      cx={x}
                      cy={y}
                      r={11}
                      fill="none"
                      stroke={color}
                      strokeWidth={1.5}
                    />
                  ) : null}
                  <circle
                    cx={x}
                    cy={y}
                    r={site.age === 'BRONZE' ? 6 : 5}
                    fill={site.age === 'GOLDEN' ? 'hsl(var(--card))' : color}
                    fillOpacity={site.age === 'SILVER' ? 0.35 : 1}
                    stroke={site.age === 'BRONZE' ? 'hsl(var(--card))' : color}
                    strokeWidth={site.age === 'BRONZE' ? 2 : 1.5}
                    strokeDasharray={site.age === 'GOLDEN' ? '3 2' : undefined}
                  >
                    <title>{`${site.name} — ${site.sub} (${AGE_LEGEND[site.age]})`}</title>
                  </circle>
                  <text
                    x={x + dx}
                    y={y + site.labelDy}
                    textAnchor={anchor}
                    fontSize={13}
                    fontWeight={600}
                    fill="hsl(var(--foreground))"
                  >
                    {site.name}
                  </text>
                  <text
                    x={x + dx}
                    y={y + site.labelDy + 13}
                    textAnchor={anchor}
                    fontSize={10}
                    fill="hsl(var(--muted-foreground))"
                  >
                    {site.sub}
                  </text>
                </g>
              );
            })}
            <g transform={`translate(${VIEWPORT.width - 170}, ${VIEWPORT.height - 60})`}>
              {(Object.keys(AGE_LEGEND) as PresenceAge[]).map((age, index) => (
                <g
                  key={age}
                  opacity={siteHighlighted(age, filter) ? 1 : 0.3}
                >
                  <circle
                    cx={6}
                    cy={index * 20}
                    r={5}
                    fill={AGE_TOKEN[age]}
                  />
                  <text
                    x={18}
                    y={index * 20 + 4}
                    fontSize={11}
                    fontWeight={600}
                    fill="hsl(var(--foreground))"
                  >
                    {AGE_LEGEND[age]}
                  </text>
                </g>
              ))}
            </g>
          </svg>
        </figure>

        <section
          className="space-y-3 rounded-lg border border-border bg-card p-5"
          data-age-view={filter}
        >
          <h2 className="text-lg font-semibold text-foreground">{view.headline}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{view.blurb}</p>
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{view.countsLabel}</p>
          <ul className="divide-y divide-border text-sm">
            {ALLOCATION.map((row) => {
              const cell = row.cells[view.column];

              return (
                <li
                  key={row.name}
                  className="grid grid-cols-[minmax(0,1fr)_3rem_3rem] items-baseline gap-2 py-1.5"
                >
                  <span>
                    <span className="block text-foreground">{row.name}</span>
                    <span className="block text-xs text-muted-foreground">{cell.what}</span>
                  </span>
                  <span className="text-end font-mono font-semibold tabular-nums">{cell.n}</span>
                  <span className="text-end font-mono text-xs text-muted-foreground">{cell.delta}</span>
                </li>
              );
            })}
          </ul>
          <p className="flex items-baseline justify-between border-t border-border pt-2 text-sm">
            <span className="text-muted-foreground">{t('board.presence.total')}</span>
            <span
              className="font-mono text-xl font-semibold tabular-nums"
              data-total-points
            >
              {view.total}
            </span>
          </p>
        </section>
      </div>

      <section className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{ALLOCATION_TITLE}</h2>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full min-w-[40rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th />
                {AGE_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cn('px-4 py-2 text-start', filter === column.key && 'bg-muted')}
                  >
                    <span className="block text-xs font-medium uppercase tracking-wide text-foreground">{column.label}</span>
                    <span className="block text-[11px] font-normal text-muted-foreground">{column.sub}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ALLOCATION.map((row) => (
                <tr
                  key={row.name}
                  className="border-b border-border"
                >
                  <th
                    scope="row"
                    className="px-4 py-2 text-start font-medium"
                  >
                    {row.name}
                  </th>
                  {row.cells.map((cell, index) => (
                    <td
                      key={AGE_COLUMNS[index]?.key}
                      className={cn('px-4 py-2 align-top', filter === AGE_COLUMNS[index]?.key && 'bg-muted')}
                    >
                      <span className="font-mono font-semibold tabular-nums">{cell.n}</span>
                      {cell.delta ? <span className="ms-1 font-mono text-xs text-muted-foreground">{cell.delta}</span> : null}
                      <span className="block text-xs text-muted-foreground">{cell.what}</span>
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <th
                  scope="row"
                  className="px-4 py-2 text-start font-medium"
                >
                  {t('board.presence.total')}
                </th>
                {ALLOCATION_TOTALS.map((total, index) => (
                  <td
                    key={AGE_COLUMNS[index]?.key}
                    className={cn('px-4 py-2 font-mono font-semibold tabular-nums', filter === AGE_COLUMNS[index]?.key && 'bg-muted')}
                  >
                    {total}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <p className="max-w-4xl text-xs leading-relaxed text-muted-foreground">{PRESENCE_NOTE}</p>
      </section>
    </div>
  );
};
